import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, RefreshCw, Trash2, Pencil, X, Globe, Sliders, Filter,
  LogOut, Users, UserCheck, Clock, Briefcase, TrendingUp,
  MessageSquare, PencilRuler, Library, Tag, Languages, Activity, Download, CheckCircle2, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import AdminHeader from '../components/admin/AdminHeader';

const API = process.env.REACT_APP_BACKEND_URL;

// Вспомогательный хелпер для безопасного форматирования ISO-дат в формат YYYY-MM-DD для инпутов
const formatDateToInput = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

// --- МАРКЕРЫ И СЛОВАРЬ ИЗ СТАРОГО КОДА МАРКЕТИНГА ---
const SOURCE_LABEL = {
  hero: 'Hero — Discover the Concept',
  b2b: 'B2B — Join Trade Program',
  header_contact: 'Header — Contact',
  contact: 'Contact form',
  registration: 'Registration',
  custom_furniture_brief: 'Custom Furniture Brief',
  lead_modal: 'Lead Capture Modal',
  unknown: 'Direct / Unknown',
};
const CATEGORY_LABEL = {
  wardrobes: 'Wardrobes (AIR LINE)',
  doors: 'Partitions & Doors',
  kitchens: 'Kitchens (RADIAL)',
  other: 'Other / Custom',
};
const ASSET_LABEL = {
  catalogue: 'Catalogues',
  '3d_model': '3D Models',
  tech_pack: 'Tech Packs',
  brochure: 'Brochures',
  specification: 'Specifications',
  other: 'Other',
};

const pretty = (map, key) => map[key] || (key ? String(key).replace(/_/g, ' ') : 'Unknown');

// ============================================================
// ВОССТАНОВЛЕННЫЙ КОМПОНЕНТ 1: Хронологический SVG График Sparkline
// ============================================================
const Sparkline = ({ data }) => {
  const W = 720;
  const H = 140;
  const P = 10;
  const rows = data || [];
  const max = Math.max(1, ...rows.map((d) => d.count));
  const n = rows.length;
  const x = (i) => P + (i * (W - 2 * P)) / Math.max(1, n - 1);
  const y = (v) => H - P - (v / max) * (H - 2 * P);
  const linePts = rows.map((d, i) => `${x(i)},${y(d.count)}`).join(' ');
  const areaPts = `${P},${H - P} ${linePts} ${W - P},${H - P}`;
  const total = rows.reduce((s, d) => s + d.count, 0);
  const peak = rows.reduce((a, b) => (b.count > a.count ? b : a), rows[0] || { count: 0, date: '' });

  return (
    <div className="border border-border p-6 bg-white rounded-lg shadow-sm" data-testid="leads-sparkline">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">Daily Leads · Last 30 Days</h3>
        </div>
        <span className="text-xs text-muted-foreground text-gray-400 font-mono" data-testid="sparkline-summary">
          {total} total · peak {peak.count}
        </span>
      </div>
      {total === 0 ? (
        <p className="text-xs text-gray-400 italic py-8 text-center">No leads in the last 30 days yet.</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36" preserveAspectRatio="none">
            <polygon points={areaPts} className="text-indigo-500/[0.04]" fill="currentColor" />
            <polyline points={linePts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" className="text-indigo-600" />
          </svg>
          <div className="flex justify-between mt-3 text-[10px] text-gray-400 font-mono">
            <span>{rows[0]?.date}</span>
            <span className="text-indigo-600 font-medium">{peak.date ? `peak: ${peak.date} (${peak.count})` : ''}</span>
            <span>{rows[rows.length - 1]?.date}</span>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// ВОССТАНОВЛЕННЫЙ КОМПОНЕНТ 2: Анимированные Столбцы Диаграмм BarList
// ============================================================
const BarList = ({ title, icon: Icon, rows, labelMap = {}, testid, empty = 'No data yet' }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-border p-6 bg-white rounded-lg shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-6">
        {Icon && <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.5} />}
        <h3 className="text-xs tracking-[0.2em] uppercase text-gray-400 font-bold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-4">{empty}</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.key} data-testid={`${testid}-row-${r.key}`}>
              <div className="flex items-center justify-between text-xs mb-1.5 font-medium text-gray-700">
                <span className="truncate pr-4">{pretty(labelMap, r.key)}</span>
                <span className="font-mono text-gray-400">{r.count}</span>
              </div>
              <div className="h-1.5 bg-slate-100 overflow-hidden rounded-full">
                <motion.div
                  className="h-full bg-slate-900 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(r.count / max) * 100}%` }}
                  transition={{ duration: 0.6 }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Базовый карточный KPI элемент
const KpiCard = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-border p-6 bg-white flex flex-col rounded-lg shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <span className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-bold">{label}</span>
      <Icon className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
    </div>
    <span className="text-3xl font-extralight tracking-tight text-gray-900 font-mono">{value}</span>
    {sub && <span className="text-[11px] text-gray-400 mt-2 font-normal">{sub}</span>}
  </div>
);

// ============================================================
// ГЛАВНЫЙ ИНТЕГРИРОВАННЫЙ СЕРВИС СТРАНИЦЫ АНАЛИТИКИ
// ============================================================
export default function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const tokenFromHook = useAuthStore((s) => s.token);
  const userFromHook = useAuthStore((s) => s.user);

  const [hydrated, setHydrated] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState('pipeline_archive'); // 'pipeline_archive' или 'marketing_funnel'
  const [loading, setLoading] = useState(true);
  const [exportingLeads, setExportingLeads] = useState(false);

  // --- СТЕЙТЫ ДЛЯ СЕКЦИИ ОРДЕРОВ ---
  const [analytics, setAnalytics] = useState([]);
  const [searchQuery, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [filterSegment, setFilterSegment] = useState('All'); 
  const [filterChannel, setFilterChannel] = useState('All'); 
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Добавлены поля хронологии создана/завершена для формы редактирования
  const [editForm, setEditForm] = useState({ 
    project_name: '', client_name: '', country: '', city: '', address: '', email: '', phone: '',
    created_at: '', completed_at: ''
  });
  const [updating, setUpdating] = useState(false);

  // --- СТЕЙТЫ ДЛЯ СЕКЦИИ МАРКЕТИНГА ---
  const [marketingData, setMarketingData] = useState(null);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/analytics' } });
    }
  }, [hydrated, navigate]);

  const fetchCompleteDashboardData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [resOrders, resMarketing] = await Promise.all([
        fetch(`${API}/api/admin/b2b/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (resOrders.ok) {
        const d = await resOrders.json();
        setAnalytics(d.analytics || []);
      }
      if (resMarketing.ok) {
        const d = await resMarketing.json();
        setMarketingData(d);
      }
    } catch (err) {
      toast.error('Critical data pool synchronization failure');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCompleteDashboardData();
  }, [token, hydrated]);

  const handleExportLeads = async () => {
    if (!token) return;
    setExportingLeads(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fedyk-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Leads database successfully exported to CSV spreadsheet');
    } catch {
      toast.error('Exporting lead structure configuration aborted');
    } finally {
      setExportingLeads(false);
    }
  };

  const dynamicCountries = useMemo(() => {
    const nations = analytics.map(item => item.country ? item.country.trim() : '');
    const unique = Array.from(new Set(nations)).filter(Boolean);
    return ['All', ...unique];
  }, [analytics]);

  const filteredOrders = useMemo(() => {
    return analytics.filter(item => {
      const lowerSearch = searchQuery.toLowerCase();
      const matchText = 
        (item.reference || '').toLowerCase().includes(lowerSearch) ||
        (item.client_name || '').toLowerCase().includes(lowerSearch) ||
        (item.project_name || '').toLowerCase().includes(lowerSearch);
      
      const matchCountry = selectedCountry === 'All' || item.country === selectedCountry;
      const matchSegment = filterSegment === 'All' || item.client_type === filterSegment;
      const matchChannel = filterChannel === 'All' || item.channel === filterChannel;

      return matchText && matchCountry && matchSegment && matchChannel;
    });
  }, [analytics, searchQuery, selectedCountry, filterSegment, filterChannel]);

  const handleEditOpen = (item) => {
    setEditingRecord(item);
    setEditForm({
      project_name: item.project_name || '', client_name: item.client_name || '',
      country: item.country || '', city: item.city || '', address: item.address || '',
      email: item.email || '', phone: item.phone || '',
      created_at: formatDateToInput(item.created_at),
      completed_at: formatDateToInput(item.completed_at)
    });
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();
    setUpdating(true);

    // Подготовка payload с приведением строковых дат к ISO формату для бэкенда
    const finalPayload = {
      ...editForm,
      created_at: editForm.created_at ? new Date(editForm.created_at).toISOString() : null,
      completed_at: editForm.completed_at ? new Date(editForm.completed_at).toISOString() : null
    };

    try {
      const res = await fetch(`${API}/api/admin/b2b/orders/${editingRecord.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(finalPayload)
      });
      if (res.ok) {
        toast.success('Changes applied to the archived document ledger');
        setEditingRecord(null);
        fetchCompleteDashboardData();
      }
    } catch {
      toast.error('Failed to update log metadata records');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id, ref) => {
    if (!window.confirm(`Permanently remove project log ${ref} from analytics database?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/b2b/orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Project track log purged successfully');
        setAnalytics(prev => prev.filter(a => a.id !== id));
      }
    } catch {
      toast.error('Failed to purge log structure');
    }
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs text-gray-400 bg-background">Loading configurations…</div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background text-left font-light">
      <SEO title="Analytics Central | FEDYK CRM" description="Comprehensive data hub" path="admin/analytics" />
      <AdminHeader active="analytics" />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        
        {/* Панель Управления Шапки */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">Global Intelligence Hub</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-gray-900">Analytics</h1>
            <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed text-gray-500">
              Comprehensive end-to-end analytics module of the FEDYK Furniture factory: managing leads, marketing funnels, and the shipped project archive.
            </p>
          </div>
          {/* ИСПРАВЛЕНО: Оформление кнопки экспорта полностью синхронизировано с кнопкой обновления */}
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleExportLeads} disabled={exportingLeads || !marketingData} className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center">
              <Download className={`w-3.5 h-3.5 mr-2 ${exportingLeads ? 'animate-pulse' : ''}`} /> Export Leads to CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchCompleteDashboardData} disabled={loading} className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center">
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh Dashboards
            </Button>
          </div>
        </div>

        {/* ГЛОБАЛЬНЫЙ ТАБ-ПЕРЕКЛЮЧАТЕЛЬ МЕЖДУ СЕКЦИЯМИ */}
        <div className="flex gap-6 border-b border-border pb-px mb-8 overflow-x-auto whitespace-nowrap scrollbar-none">
          <button
            onClick={() => setActiveViewTab('pipeline_archive')}
            className={`flex items-center gap-2 pb-3 text-xs tracking-wider uppercase font-medium border-b transition-all ${
              activeViewTab === 'pipeline_archive' ? 'border-foreground text-foreground font-semibold border-slate-900' : 'border-transparent text-muted-foreground text-gray-400 hover:text-foreground'
            }`}
          >
            <Briefcase className="w-4 h-4" /> 1. Shipped Projects Archive ({analytics.length})
          </button>

          <button
            onClick={() => setActiveViewTab('marketing_funnel')}
            className={`flex items-center gap-2 pb-3 text-xs tracking-wider uppercase font-medium border-b transition-all ${
              activeViewTab === 'marketing_funnel' ? 'border-foreground text-foreground font-semibold border-slate-900' : 'border-transparent text-muted-foreground text-gray-400 hover:text-foreground'
            }`}
          >
            <TrendingUp className="w-4 h-4" /> 2. Marketing & Lead Generation ({marketingData?.leads?.total || 0})
          </button>
        </div>

        {/* ОСНОВНОЙ РЕНДЕР КОНТЕНТА ПО ВКЛАДКАМ */}
        {loading ? (
          <div className="py-24 text-center text-gray-400 text-xs">Assembling system charts and log matrix...</div>
        ) : (
          <AnimatePresence mode="wait">
            
            {/* ВКЛАДКА 1: АРХИВ ОРДЕРОВ */}
            {activeViewTab === 'pipeline_archive' && (
              <motion.div key="pipeline" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                
                {/* Карточки KPI для Ордеров */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard icon={CheckCircle2} label="Completed Projects" value={analytics.length} sub="Pipeline Archive" />
                  <KpiCard icon={Users} label="B2B Partner Share" value={analytics.length ? `${Math.round((analytics.filter(i => i.client_type === 'b2b').length / analytics.length) * 100)}%` : '0%'} sub="Studios & Designers" />
                  <KpiCard icon={Globe} label="Online Placement" value={analytics.length ? `${Math.round((analytics.filter(i => i.channel === 'online').length / analytics.length) * 100)}%` : '0%'} sub="Orders via Partner Portal" />
                  <KpiCard icon={Clock} label="Geo Hubs" value={dynamicCountries.length - 1} sub="Active Sales Regions" />
                </div>

                {/* Блок настройки фильтров ордеров */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 bg-white p-4 border border-gray-100 rounded-lg shadow-sm text-xs">
                  <div>
                    <label className="block text-[10px] tracking-wider uppercase font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><Search className="w-3 h-3" /> Text Search</label>
                    <input type="text" placeholder="Ref, client identity, spec..." value={searchQuery} onChange={(e) => setSearchTerm(e.target.value)} className="w-full h-10 px-3 border border-neutral-200 focus:outline-none focus:border-black font-medium text-gray-900 bg-white" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-wider uppercase font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><Globe className="w-3 h-3" /> Region / Country</label>
                    <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="w-full h-10 px-2 border border-neutral-200 focus:outline-none focus:border-black font-semibold text-gray-900 bg-white cursor-pointer">
                      <option value="All">All Countries ({dynamicCountries.length - 1})</option>
                      {dynamicCountries.map(c => c !== 'All' && <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-wider uppercase font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><Filter className="w-3 h-3" /> Market Segment</label>
                    <select value={filterSegment} onChange={(e) => setFilterSegment(e.target.value)} className="w-full h-10 px-2 border border-neutral-200 focus:outline-none focus:border-black font-semibold text-gray-900 bg-white cursor-pointer">
                      <option value="All">All Groups (B2B + B2C)</option>
                      <option value="b2b">B2B Trade Accounts Only</option>
                      <option value="b2c">B2C Retail Customers Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-wider uppercase font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><Sliders className="w-3 h-3" /> Sales Channel</label>
                    <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)} className="w-full h-10 px-2 border border-neutral-200 focus:outline-none focus:border-black font-semibold text-gray-900 bg-white cursor-pointer">
                      <option value="All">All Sales Channels</option>
                      <option value="online">Online E-Commerce Platform</option>
                      <option value="offline">Offline Direct Showroom</option>
                    </select>
                  </div>
                </div>

                {/* Таблица архивного конвейера */}
                {filteredOrders.length === 0 ? (
                  <div className="border border-dashed border-gray-200 p-12 text-center bg-gray-50 text-xs text-gray-400 rounded-lg">No project logs matched the filtering criteria.</div>
                ) : (
                  <div className="border border-border overflow-x-auto bg-white rounded-lg shadow-sm">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-gray-50 border-b border-border text-gray-400 font-semibold uppercase tracking-wider text-[10px]">
                        <tr className="text-left">
                          <th className="px-5 py-4 w-32">Reference</th>
                          <th className="px-5 py-4 w-36">Timeline</th>
                          <th className="px-5 py-4">Client Registry</th>
                          <th className="px-5 py-4">Destination Target</th>
                          <th className="px-5 py-4">Specification</th>
                          <th className="px-5 py-4">Classification</th>
                          <th className="px-5 py-4 w-24"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border text-gray-700">
                        {filteredOrders.map((item) => (
                          <tr key={item.id} className="hover:bg-neutral-50/50 transition-colors">
                            <td className="px-5 py-4 font-mono font-bold text-indigo-600 text-sm">{item.reference}</td>
                            <td className="px-5 py-4 whitespace-nowrap leading-relaxed font-mono text-[11px] text-gray-500">
                              <span className="text-emerald-600">In:</span> {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}<br />
                              <span className="text-slate-900">Out:</span> {item.completed_at ? new Date(item.completed_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-5 py-4">
                              <p className="font-semibold text-gray-900 text-sm">{item.client_name || '—'}</p>
                              <p className="font-mono text-gray-400 text-[10px] mt-0.5">{item.email} {item.phone}</p>
                            </td>
                            <td className="px-5 py-4 leading-tight">
                              <p className="font-semibold text-gray-800">{item.country}</p>
                              <p className="text-gray-400 mt-0.5 text-[11px] font-normal">{item.city}, {item.address || '—'}</p>
                            </td>
                            <td className="px-5 py-4 font-medium text-gray-900">{item.project_name}</td>
                            <td className="px-5 py-4 font-mono text-[10px] uppercase font-bold whitespace-nowrap">
                              <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[9px] mr-1">{item.client_type}</span>
                              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px]">{item.channel}</span>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <div className="flex items-center gap-1.5 justify-end">
                                <button type="button" onClick={() => handleEditOpen(item)} className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all p-1.5"><Pencil className="w-3.5 h-3.5" /></button>
                                <button type="button" onClick={() => handleDelete(item.id, item.reference)} className="text-gray-400 hover:text-red-600 hover:bg-rose-50 rounded transition-all p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* ВКЛАДКА 2: МАРКЕТИНГОВАЯ АНАЛИТИКА */}
            {activeViewTab === 'marketing_funnel' && marketingData && (
              <motion.div key="marketing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard icon={Users} label="Total Leads" value={marketingData.leads?.total || 0} sub={`${marketingData.leads?.today || 0} today · ${marketingData.leads?.last_7d || 0} this week`} />
                  <KpiCard icon={TrendingUp} label="Leads · 30 Days" value={marketingData.leads?.last_30d || 0} sub="rolling window cluster" />
                  <KpiCard icon={Clock} label="B2B Pending" value={marketingData.b2b?.pending || 0} sub={`${marketingData.b2b?.approved || 0} approved accounts`} />
                  <KpiCard icon={Briefcase} label="Trade Requests" value={marketingData.trade_pricing?.total || 0} sub={`${marketingData.trade_pricing?.last_30d || 0} in 30 days`} />
                </div>

                <Sparkline data={marketingData.leads?.daily} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <BarList title="Leads by Source" icon={TrendingUp} rows={marketingData.leads?.by_source || []} labelMap={SOURCE_LABEL} testid="leads-by-source" />
                  <BarList title="Leads by Category" icon={Tag} rows={marketingData.leads?.by_category || []} labelMap={CATEGORY_LABEL} testid="leads-by-category" />
                  <BarList title="Leads by Language" icon={Languages} rows={marketingData.leads?.by_language || []} testid="leads-by-language" />
                  <BarList title="Leads by Country" icon={Globe} rows={marketingData.leads?.by_country || []} testid="leads-by-country" />
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  <KpiCard icon={UserCheck} label="B2B Approved" value={marketingData.b2b?.approved || 0} sub="Verified architectural studios" />
                  <KpiCard icon={Users} label="Customers" value={marketingData.b2b?.customers || 0} sub="End retail consumers database" />
                  <KpiCard icon={MessageSquare} label="Contact Inquiries" value={marketingData.engagement?.contact_inquiries || 0} sub="Direct landing forms logs" />
                  <KpiCard icon={PencilRuler} label="Custom Briefs" value={marketingData.engagement?.custom_briefs || 0} sub="Bespoke order forms configuration" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <BarList title="Trade Pricing · Status Logs" icon={Briefcase} rows={marketingData.trade_pricing?.by_status || []} testid="trade-pricing-status" empty="No requests logged" />
                  <BarList title="Trade Library · Asset Type Breakdown" icon={Library} rows={marketingData.trade_library?.assets_by_category || []} labelMap={ASSET_LABEL} testid="library-by-type" empty="No assets uploaded yet" />
                </div>

                <div className="border border-border bg-white rounded-lg shadow-sm" data-testid="recent-leads">
                  <div className="px-6 py-4 border-b border-border flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">Recent Platform Leads</h3>
                  </div>
                  {(marketingData.leads?.recent || []).length === 0 ? (
                    <p className="text-xs text-gray-400 italic p-6">No leads captured yet.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {marketingData.leads.recent.map((r) => (
                        <div key={r.id} className="grid grid-cols-12 gap-3 px-6 py-3.5 text-xs items-center" data-testid={`recent-lead-${r.id}`}>
                          <span className="col-span-3 font-semibold text-slate-800 truncate">{r.name || '—'}</span>
                          <span className="col-span-3 text-gray-400 font-mono truncate">{r.phone || r.email || '—'}</span>
                          <span className="col-span-2 text-gray-500 truncate">{pretty(CATEGORY_LABEL, r.category)}</span>
                          <span className="col-span-2 text-gray-500 truncate">{pretty(SOURCE_LABEL, r.source)}</span>
                          <span className="col-span-2 text-gray-400 font-mono text-right">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </motion.div>
            )}

          </AnimatePresence>
        )}

      </div>

      {/* МОДАЛЬНОЕ ОКНО РЕДАКТИРОВАНИЯ */}
      <AnimatePresence>
        {editingRecord && (
          <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 overflow-y-auto">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 15 }} className="bg-white border border-border rounded-none shadow-xl w-full max-w-md p-6">
              <div className="flex items-start justify-between border-b border-gray-100 pb-4 mb-4">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-gray-400 font-semibold">CRM Correction Engine</p>
                  <h3 className="text-lg font-light text-gray-900 truncate max-w-[300px]">Modify {editingRecord.reference}</h3>
                </div>
                <button onClick={() => setEditingRecord(null)} className="text-gray-400 hover:text-black"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleUpdateRecord} className="space-y-4 text-xs text-left">
                <div>
                  <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">Item Specification</label>
                  <input type="text" required value={editForm.project_name} onChange={(e) => setEditForm(p => ({ ...p, project_name: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] text-gray-900 font-medium focus:outline-none focus:border-black rounded-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">Client / Company Identity</label>
                  <input type="text" value={editForm.client_name} onChange={(e) => setEditForm(p => ({ ...p, client_name: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] text-gray-900 font-medium focus:outline-none focus:border-black rounded-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="email" placeholder="Email Contact" value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] focus:outline-none focus:border-black text-gray-900 rounded-none" />
                  <input type="text" placeholder="Phone Index" value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] focus:outline-none focus:border-black text-gray-900 rounded-none" />
                </div>
                
                {/* НОВЫЙ БЛОК: Ручное управление временной шкалой проекта (Start & Completion Dates) */}
                <div className="grid grid-cols-2 gap-3 bg-neutral-50 p-3 border border-neutral-100">
                  <div>
                    <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">Start Date (In)</label>
                    <input type="date" value={editForm.created_at} onChange={(e) => setEditForm(p => ({ ...p, created_at: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-gray-900 focus:outline-none focus:border-black rounded-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">Completion Date (Out)</label>
                    <input type="date" value={editForm.completed_at} onChange={(e) => setEditForm(p => ({ ...p, completed_at: e.target.value }))} className="w-full px-2.5 h-9 border border-[#ECECEC] bg-white text-gray-900 focus:outline-none focus:border-black rounded-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Country" value={editForm.country} onChange={(e) => setEditForm(p => ({ ...p, country: e.target.value }))} className="w-full px-3 h-10 border border-neutral-300 font-bold text-gray-900 focus:outline-none focus:border-black rounded-none" />
                  <input type="text" placeholder="City" value={editForm.city} onChange={(e) => setEditForm(p => ({ ...p, city: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] focus:outline-none focus:border-black text-gray-900 rounded-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">Exact Shipping Destination</label>
                  <input type="text" placeholder="Street name, suite, warehouse indices" value={editForm.address} onChange={(e) => setEditForm(p => ({ ...p, address: e.target.value }))} className="w-full px-3 h-10 border border-[#ECECEC] focus:outline-none focus:border-black text-gray-900 rounded-none" />
                </div>
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100 mt-6">
                  <Button type="button" variant="outline" size="sm" className="rounded-none h-10 px-4 text-xs bg-white text-gray-900 border-gray-200" onClick={() => setEditingRecord(null)} disabled={updating}>Cancel</Button>
                  <button type="submit" disabled={updating} className="inline-flex items-center justify-center bg-black text-white text-xs px-5 h-10 font-medium transition-colors rounded-none shadow-sm">
                    {updating ? 'Saving...' : 'Correct Metadata'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
